/**
 * useDocumentDetailQuery — fetch single document metadata.
 *
 * Endpoint: GET /api/v1/documents/{id}
 *
 * Returns the row from `document.documents` joined with `org.departments`
 * + `document.retention_policies` for human-readable labels.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface DocumentDetail {
  document_id: string;
  doc_number: string;
  title: string;
  classification: string;
  document_type: string;
  document_type_label?: string;
  owner_user_id: string;
  owner_name?: string | null;
  department_id: string | null;
  department_name?: string | null;
  department?: string | null;
  status: string;
  site_id: string | null;
  site_code?: string | null;
  retention_policy_id: string | null;
  retention_policy_name?: string | null;
  source_artifact_ref: string | null;
  source_artifact_mime: string | null;
  legal_hold_active: boolean;
  created_at_utc: string;
  updated_at_utc: string;
  retention_end_at: string | null;
}

export function useDocumentDetailQuery(
  documentId: string | undefined
): UseQueryResult<DocumentDetail> {
  return useQuery<DocumentDetail>({
    queryKey: ['documents', documentId],
    queryFn: ({ signal }) =>
      apiFetch<DocumentDetail>(`/api/v1/documents/${encodeURIComponent(documentId!)}`, { signal }),
    enabled: !!documentId,
    staleTime: 60_000,
  });
}

// P4 / DMS-30 — the caller's document-visibility scope, used to guard the
// (Spring-served) detail-by-UUID screen. The list + search are enforced
// server-side; this closes the direct-URL gap at the app layer.
export interface VisibleSites { scoped: boolean; site_ids: string[] | null }
export function useDocVisibleSites(): UseQueryResult<VisibleSites> {
  return useQuery<VisibleSites>({
    queryKey: ['documents', 'visible-sites'],
    queryFn: ({ signal }) => apiFetch<VisibleSites>('/api/v1/documents/visible-sites', { signal }),
    staleTime: 300_000,
  });
}
// True if a document at `siteId` is visible to the caller. Default-allow while
// loading / when unscoped (consistent with the server-side safe default).
export function isDocVisible(vis: VisibleSites | undefined, siteId: string | null): boolean {
  if (!vis || !vis.scoped || vis.site_ids === null) return true;
  if (siteId === null) return true; // global (null-site) docs are visible to everyone
  return vis.site_ids.includes(siteId);
}

// B-6a — effective-date control. A GMP document does NOT become effective the
// moment QA approves it: it gets a future effective date (default +7 days) so
// training can complete first. The planned date lives on the latest version;
// the doc flips to EFFECTIVE on/after that date (lazily, server-side).
export interface EffectivityPlan {
  document_id: string | null;
  status: string | null;
  version: string | null;
  effective_date: string | null; // ISO date (or null = not scheduled)
  days_remaining: number | null;  // signed; negative = past due (about to flip)
}
export function useEffectivityPlan(documentId: string | undefined): UseQueryResult<EffectivityPlan> {
  return useQuery<EffectivityPlan>({
    queryKey: ['documents', documentId, 'effectivity-plan'],
    queryFn: ({ signal }) =>
      apiFetch<EffectivityPlan>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/effectivity-plan`,
        { signal }
      ),
    enabled: !!documentId,
    staleTime: 30_000,
  });
}

// Schedule the effective date: status → APPROVED_PENDING_EFFECTIVE, version's
// effective_date = today + leadDays. Invalidates the detail + plan so the
// lifecycle stepper / banner repaint immediately.
export function useScheduleEffective(
  documentId: string | undefined
): UseMutationResult<EffectivityPlan, Error, { leadDays: number }> {
  const qc = useQueryClient();
  return useMutation<EffectivityPlan, Error, { leadDays: number }>({
    mutationFn: ({ leadDays }) =>
      apiFetch<EffectivityPlan>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/approve-effective`,
        { method: 'POST', body: { effectiveDelayDays: leadDays } }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', documentId] });
    },
  });
}

// B-6b — version history + supersession. A controlled document keeps every
// version; only ONE is EFFECTIVE at a time. When a new version becomes
// effective, the prior effective version is auto-superseded (read-only).
export interface DocumentVersion {
  document_version_id: string;
  version: string;
  state: string;           // DRAFT | IN_REVIEW | APPROVED_PENDING_EFFECTIVE | EFFECTIVE | SUPERSEDED | …
  effective_date: string | null;
  created_at_utc: string;
  author_name: string;
}
export function useDocumentVersions(
  documentId: string | undefined
): UseQueryResult<{ items: DocumentVersion[]; total: number }> {
  return useQuery<{ items: DocumentVersion[]; total: number }>({
    queryKey: ['documents', documentId, 'versions'],
    queryFn: ({ signal }) =>
      apiFetch<{ items: DocumentVersion[]; total: number }>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/versions`,
        { signal }
      ),
    enabled: !!documentId,
    staleTime: 30_000,
  });
}

// Start a revision: creates the next DRAFT version and sends the doc back to
// DRAFT. The current effective version stays the controlled copy until the new
// one becomes effective (then the sweep supersedes it).
export function useReviseDocument(
  documentId: string | undefined
): UseMutationResult<{ status: string; new_version: string }, Error, void> {
  const qc = useQueryClient();
  return useMutation<{ status: string; new_version: string }, Error, void>({
    mutationFn: () =>
      apiFetch<{ status: string; new_version: string }>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/revise`,
        { method: 'POST', body: {} }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', documentId] });
    },
  });
}

// B-6c — linked documents. An SOP and its forms / work instructions are linked
// so they're reviewed together; when one is revised the others are flagged.
export interface DocumentLink {
  link_id: string;
  link_kind: string;       // FORM | WORK_INSTRUCTION | REFERENCE | ATTACHMENT | TEMPLATE | SUPERSEDES
  note: string | null;
  created_at_utc: string;
  document_id: string;     // the OTHER document
  doc_number: string;
  title: string;
  status: string;
}
export interface DocumentLinks {
  children: DocumentLink[]; // forms / WIs this document governs
  parents: DocumentLink[];  // SOPs that reference this document
  total: number;
}
export function useDocumentLinks(
  documentId: string | undefined
): UseQueryResult<DocumentLinks> {
  return useQuery<DocumentLinks>({
    queryKey: ['documents', documentId, 'links'],
    queryFn: ({ signal }) =>
      apiFetch<DocumentLinks>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/links`,
        { signal }
      ),
    enabled: !!documentId,
    staleTime: 30_000,
  });
}
export function useAddDocumentLink(
  documentId: string | undefined
): UseMutationResult<DocumentLink, Error, { childDocNumber: string; linkKind: string }> {
  const qc = useQueryClient();
  return useMutation<DocumentLink, Error, { childDocNumber: string; linkKind: string }>({
    mutationFn: ({ childDocNumber, linkKind }) =>
      apiFetch<DocumentLink>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/links`,
        { method: 'POST', body: { childDocNumber, linkKind } }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', documentId, 'links'] });
    },
  });
}
export function useRemoveDocumentLink(
  documentId: string | undefined
): UseMutationResult<{ ok: boolean }, Error, string> {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, string>({
    mutationFn: (linkId) =>
      apiFetch<{ ok: boolean }>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/links/${encodeURIComponent(linkId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', documentId, 'links'] });
    },
  });
}
